import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ProductType3AGuard } from '@app/guards/3a-users.guard';
import { AuthGuard } from '@app/guards/auth.guard';
import { CrowdInvestorGuard } from '@app/guards/crowd-investor.guard';
import { GermanyNotAllowedGuard } from '@app/guards/germany-not-allowed.guard';
import { IdVerificationGuard } from '@app/guards/idverification.guard';
import { LoginGuard } from '@app/guards/login.guard';
import { MobileAppGuard } from '@app/guards/mobile-app.guard';
import { canActivateOdd } from '@app/guards/odd.guard';
import { ResetPasswordGuard } from '@app/guards/reset-password.guard';
import { SwissRegisterAllowedGuard } from '@app/guards/swiss-register-allowed.guard';
import { VerifyStatusGuard } from '@app/guards/verify-status.guard';
import { VerifyComponent } from '@app/verify/verify.component';
import { GreenSurveyComponent } from '@shared/components';
import { AppAccountOpeningBlockerComponent } from '@shared/components/account-opening-blocker/account-opening-blocker.component';
import { PromoCampaignComponent } from '@shared/components/promo-campaign/promo-campaign.component';

import { EditPasswordComponent } from './edit-password/edit-password.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';
import { RegisterFriendComponent } from './register-friend/register-friend.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

const routes: Routes = [
  { path: '', component: HomeComponent, canActivate: [AuthGuard] },
  // public urls
  { path: 'login', component: LoginComponent, canActivate: [LoginGuard] },
  { path: 'profil-erstellen', component: RegisterComponent, canActivate: [SwissRegisterAllowedGuard] },
  { path: 'create-profile', component: RegisterComponent, canActivate: [SwissRegisterAllowedGuard] },
  { path: 'creer-profil', component: RegisterComponent, canActivate: [SwissRegisterAllowedGuard] },
  { path: 'invite/:code', component: RegisterFriendComponent, canActivate: [SwissRegisterAllowedGuard] },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'password/edit', component: EditPasswordComponent, canActivate: [ResetPasswordGuard] },
  { path: 'verify/:status', component: VerifyComponent, canActivate: [VerifyStatusGuard] },
  // end public urls
  { path: 'mobileapp', component: RegisterComponent, canActivate: [MobileAppGuard] },
  { path: 'idnow/complete', component: LoginComponent, canActivate: [IdVerificationGuard] },
  { path: 'ident/complete', component: LoginComponent, canActivate: [IdVerificationGuard] },
  { path: 'promo-campaign/:id', component: PromoCampaignComponent, canActivate: [AuthGuard] },

  {
    path: 'complete-bank-setup',
    component: AppAccountOpeningBlockerComponent,
    canActivate: [AuthGuard]
  },

  // Deep links redirects - can't be absolute redirect, cause query params not preserving
  { path: 'product/account', redirectTo: 'profile/settings' },
  { path: 'product/account/documents', redirectTo: 'profile/documents' },
  { path: 'product/account/invite-friends', redirectTo: 'tell-friend' },
  { path: 'product/crowdinvesting', redirectTo: 'ci-overview' },
  { path: 'product/home', redirectTo: 'reports/dashboard' },
  { path: 'product/performance', redirectTo: 'reports/dashboard' },
  { path: 'product/grow-performance', redirectTo: 'grow' },
  { path: 'product/impact', redirectTo: 'reports/dashboard' },
  { path: 'product/home/impact-news/:id', redirectTo: 'reports/dashboard' },
  { path: 'product/home/campaign/:id', redirectTo: 'reports/dashboard' },
  { path: 'product/account/strategy', redirectTo: 'strategy' },
  { path: 'product/account/strategy/portfolio-list', redirectTo: 'strategy' },
  { path: 'product/account/transactions', redirectTo: 'reports/transactions' },
  { path: 'product/account/invest-money', redirectTo: 'adjust-investment' },
  { path: 'product/account/invest-details', redirectTo: 'adjust-investment' },
  { path: 'product/account/withdraw', redirectTo: 'adjust-investment' },
  {
    path: 'product/account/transfer-3a',
    redirectTo: '/adjust-investment?accountDetails=true'
  },
  // New modules structure
  {
    path: 'strategy',
    loadChildren: () => import('./modules/strategy/strategy.module').then((m) => m.StrategyModule)
  },
  {
    path: 'grow',
    loadChildren: () => import('./modules/grow/grow.module').then((m) => m.GrowModule)
  },
  {
    path: 'profile',
    loadChildren: () => import('./modules/profile/profile.module').then((m) => m.ProfileModule)
  },
  {
    path: 'reports',
    loadChildren: () => import('./modules/reporting/reporting.module').then((m) => m.ReportingModule)
  },
  {
    path: 'ci-overview',
    canActivate: [AuthGuard, CrowdInvestorGuard],
    loadChildren: () => import('./modules/ci-overview/ci-overview.module').then((m) => m.CiOverviewModule)
  },
  {
    path: 'next/saxo-account',
    canActivate: [AuthGuard, GermanyNotAllowedGuard],
    loadChildren: () => import('./modules/ao-ch/ao-ch.module').then((m) => m.AOCHModule)
  },
  {
    path: '3a/account-opening',
    canActivate: [AuthGuard, GermanyNotAllowedGuard, ProductType3AGuard],
    loadChildren: () => import('./modules/ao-3a/ao-3a.module').then((m) => m.AO3AModule)
  },
  {
    path: 'kids',
    canActivate: [AuthGuard, GermanyNotAllowedGuard],
    loadChildren: () => import('./modules/kids/kids.module').then((m) => m.KidsModule)
  },
  {
    path: 'next/finish-verification',
    canActivate: [AuthGuard],
    loadChildren: () => import('./modules/verification/verification.module').then((m) => m.VerificationModule)
  },
  {
    path: 'odd',
    canActivate: [AuthGuard, GermanyNotAllowedGuard, canActivateOdd],
    loadChildren: () => import('@app/modules/odd/odd.module').then((m) => m.OddModule)
  },
  {
    path: 'greenness-survey',
    canActivate: [AuthGuard],
    component: GreenSurveyComponent
  },
  { path: '**', redirectTo: '/', pathMatch: 'full' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      scrollPositionRestoration: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
